import { useState, useEffect, forwardRef, type ComponentProps } from 'react'
import { Input } from '@/src/components/ui/forms/inputs/Input'
import { useMaskedInputProps } from 'react-native-mask-input'
import type { MaskArray } from 'react-native-mask-input/src/formatWithMask.types'
import type { TextInput } from 'react-native'

interface BirthdateInputProps extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
  value: Date | undefined
  onChange?: (date: Date | undefined) => void
}

// Mask pattern for MM-DD-YYYY format
const birthdateMask: MaskArray = [
  [/\d/],
  [/\d/],
  '-',
  [/\d/],
  [/\d/],
  '-',
  [/\d/],
  [/\d/],
  [/\d/],
  [/\d/],
]

// Function to format a Date object to MM-DD-YYYY string
const formatDateToString = (date: Date | undefined): string => {
  if (!date) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}-${day}-${year}`
}

// Function to parse MM-DD-YYYY string to Date object
const parseDateFromString = (dateStr: string): Date | undefined => {
  const regex = /^(\d{2})-(\d{2})-(\d{4})$/
  const match = dateStr.match(regex)
  if (!match) return undefined

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])

  if (isNaN(month) || isNaN(day) || isNaN(year)) {
    return undefined
  }

  const parsedDate = new Date(year, month - 1, day)

  // Validate if the parsed date is valid
  return parsedDate.getMonth() + 1 === month &&
    parsedDate.getDate() === day &&
    parsedDate.getFullYear() === year
    ? parsedDate
    : undefined
}

export const BirthdateInput = forwardRef<TextInput, BirthdateInputProps>(
  ({ onChange, value, placeholder, info, ...restOfProps }, ref) => {
    const [internalValue, setInternalValue] = useState(formatDateToString(value))
    const [error, setError] = useState<string>()

    useEffect(() => {
      setInternalValue(formatDateToString(value))
    }, [value])

    const maskedInputProps = useMaskedInputProps({
      value: internalValue,
      mask: birthdateMask,
      onChangeText: (masked) => {
        setInternalValue(masked)
        const parsedDate = parseDateFromString(masked)

        if (!parsedDate && masked.length === 10) {
          setError('Invalid date. Please use MM-DD-YYYY format.')
        } else {
          setError('')
        }

        onChange && onChange(parsedDate)
      },
    })

    return (
      <Input
        {...restOfProps}
        {...maskedInputProps}
        placeholder={placeholder || 'Date of birth (MM-DD-YYYY)'}
        keyboardType="numeric"
        info={info || (error ? { error } : undefined)}
        ref={ref}
      />
    )
  },
)

export default BirthdateInput
